import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiEndpoint } from '../../../config/api';
import { getUserRole } from '../../../utils/roleUtils';
import {
  FaArrowLeft, FaPaperclip, FaPaperPlane, FaClock, FaUser, FaHeadset,
  FaLock, FaCheckCircle, FaTimes, FaExclamationTriangle, FaDownload
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import MainHeader from '../../shared/MainHeader/MainHeader';
import './Support.css';

const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', waiting_on_user: 'Waiting on You',
  resolved: 'Resolved', closed: 'Closed'
};
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const CATEGORY_LABELS = {
  general: 'General', bug_report: 'Bug Report', feature_request: 'Feature Request',
  access_issue: 'Access Issue', billing: 'Billing', compliance: 'Compliance',
  data_request: 'Data Request', onboarding: 'Onboarding', other: 'Other'
};

const SupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [msgFiles, setMsgFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);

  const role = getUserRole();
  const isStaff = ['cloud_staff', 'super_admin'].includes(role);
  const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

  const fetchTicket = async () => {
    try {
      const res = await fetch(getApiEndpoint(`/api/support/tickets/${id}`), { headers });
      if (!res.ok) {
        toast.error('Ticket not found');
        navigate('/support/tickets');
        return;
      }
      const data = await res.json();
      setTicket(data.ticket);
      setMessages(data.messages || []);
      setAttachments(data.attachments || []);
    } catch (e) {
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]);

  useEffect(() => {
    if (isStaff) {
      fetch(getApiEndpoint('/api/support/tickets/admin/staff'), { headers })
        .then(r => r.ok ? r.json() : [])
        .then(setStaffList)
        .catch(() => {});
    }
  }, [isStaff]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', newMessage.trim());
      if (isStaff && isInternal) formData.append('is_internal', 'true');
      msgFiles.forEach(f => formData.append('attachments', f));

      const res = await fetch(getApiEndpoint(`/api/support/tickets/${id}/messages`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (res.ok) {
        setNewMessage('');
        setMsgFiles([]);
        setIsInternal(false);
        await fetchTicket();
      } else {
        const err = await res.json();
        toast.error(err.message || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/support/tickets/${id}/status`), {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        toast.success(`Status updated to ${STATUS_LABELS[status]}`);
        await fetchTicket();
      }
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const updateAssignment = async (assignedTo) => {
    try {
      await fetch(getApiEndpoint(`/api/support/tickets/${id}/assign`), {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: assignedTo || null })
      });
      await fetchTicket();
    } catch (e) {
      toast.error('Failed to assign ticket');
    }
  };

  const updatePriority = async (priority) => {
    try {
      await fetch(getApiEndpoint(`/api/support/tickets/${id}/priority`), {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority })
      });
      await fetchTicket();
    } catch (e) {
      toast.error('Failed to update priority');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="sup-page">
        <MainHeader />
        <div className="sup-container">
          <div className="sup-loading"><div className="sup-spinner" /><p>Loading ticket...</p></div>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const isClosed = ['resolved', 'closed'].includes(ticket.status);

  return (
    <div className="sup-page">
      <MainHeader />
      <div className="sup-container sup-detail-container">
      {/* Header */}
      <div className="sup-detail-header">
        <button className="sup-btn sup-btn-ghost" onClick={() => navigate(isStaff ? '/cloud-staff/support' : '/support/tickets')}>
          <FaArrowLeft /> Back
        </button>
        <div className="sup-detail-title-row">
          <span className="sup-ticket-ref">{ticket.ticket_ref}</span>
          <span className={`sup-badge sup-badge-status sup-status-${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
          <span className={`sup-badge sup-badge-priority sup-priority-${ticket.priority}`}>
            {ticket.priority === 'urgent' && <FaExclamationTriangle />} {PRIORITY_LABELS[ticket.priority]}
          </span>
        </div>
      </div>

      <div className="sup-detail-layout">
        {/* Main Thread */}
        <div className="sup-detail-main">
          {/* Original Ticket */}
          <div className="sup-message sup-message-user sup-message-original">
            <div className="sup-message-header">
              <div className="sup-message-author">
                <FaUser className="sup-avatar-icon" />
                <span>{ticket.user_first_name} {ticket.user_last_name}</span>
              </div>
              <span className="sup-message-time"><FaClock /> {formatDate(ticket.created_at)}</span>
            </div>
            <h3 className="sup-message-subject">{ticket.subject}</h3>
            <div className="sup-message-body">{ticket.description}</div>
            {attachments.length > 0 && (
              <div className="sup-message-attachments">
                {attachments.map(a => (
                  <a key={a.id} href={getApiEndpoint(`/uploads/support/${a.stored_filename}`)} target="_blank" rel="noreferrer" className="sup-attachment-chip">
                    <FaDownload /> {a.original_filename} <span className="sup-att-size">({formatSize(a.file_size)})</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          {messages.map(m => {
            const isStaffMsg = m.sender_role === 'staff';
            const parsedAttachments = m.attachments ? (typeof m.attachments === 'string' ? JSON.parse(m.attachments) : m.attachments) : [];
            const validAttachments = Array.isArray(parsedAttachments) ? parsedAttachments.filter(a => a && a.id) : [];
            return (
              <div key={m.id} className={`sup-message ${isStaffMsg ? 'sup-message-staff' : 'sup-message-user'} ${m.is_internal ? 'sup-message-internal' : ''}`}>
                <div className="sup-message-header">
                  <div className="sup-message-author">
                    {isStaffMsg ? <FaHeadset className="sup-avatar-icon sup-avatar-staff" /> : <FaUser className="sup-avatar-icon" />}
                    <span>{m.first_name} {m.last_name}</span>
                    {isStaffMsg && <span className="sup-staff-badge">Support</span>}
                    {m.is_internal && <span className="sup-internal-badge"><FaLock /> Internal</span>}
                  </div>
                  <span className="sup-message-time"><FaClock /> {formatDate(m.created_at)}</span>
                </div>
                <div className="sup-message-body">{m.message}</div>
                {validAttachments.length > 0 && (
                  <div className="sup-message-attachments">
                    {validAttachments.map(a => (
                      <a key={a.id} href={getApiEndpoint(`/uploads/support/${a.stored_filename}`)} target="_blank" rel="noreferrer" className="sup-attachment-chip">
                        <FaDownload /> {a.original_filename} <span className="sup-att-size">({formatSize(a.file_size)})</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={messagesEndRef} />

          {/* Reply Box */}
          {!isClosed ? (
            <form onSubmit={sendMessage} className="sup-reply-box">
              {isStaff && (
                <div className="sup-reply-mode">
                  <button type="button" className={`sup-mode-btn ${!isInternal ? 'active' : ''}`} onClick={() => setIsInternal(false)}>
                    Reply to User
                  </button>
                  <button type="button" className={`sup-mode-btn sup-mode-internal ${isInternal ? 'active' : ''}`} onClick={() => setIsInternal(true)}>
                    <FaLock /> Internal Note
                  </button>
                </div>
              )}
              <textarea
                className={`sup-reply-input ${isInternal ? 'sup-reply-internal' : ''}`}
                placeholder={isInternal ? 'Add an internal note (hidden from user)...' : 'Type your reply...'}
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                rows={3}
              />
              <div className="sup-reply-actions">
                <label className="sup-btn sup-btn-ghost sup-btn-sm">
                  <FaPaperclip /> Attach
                  <input type="file" multiple onChange={e => setMsgFiles(Array.from(e.target.files))} className="sup-file-input" />
                </label>
                {msgFiles.length > 0 && (
                  <span className="sup-file-count">{msgFiles.length} file{msgFiles.length > 1 ? 's' : ''}</span>
                )}
                <button type="submit" className="sup-btn sup-btn-primary sup-btn-sm" disabled={sending || !newMessage.trim()}>
                  <FaPaperPlane /> {sending ? 'Sending...' : isInternal ? 'Add Note' : 'Send'}
                </button>
              </div>
            </form>
          ) : (
            <div className="sup-closed-banner">
              <FaCheckCircle /> This ticket is {ticket.status}.
              {!isStaff && (
                <button className="sup-btn sup-btn-ghost sup-btn-sm" onClick={() => updateStatus('open')}>
                  Reopen
                </button>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="sup-detail-sidebar">
          <div className="sup-sidebar-section">
            <h4>Details</h4>
            <div className="sup-sidebar-row">
              <span className="sup-sidebar-label">Status</span>
              {isStaff ? (
                <select className="sup-sidebar-select" value={ticket.status} onChange={e => updateStatus(e.target.value)}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              ) : (
                <span className={`sup-badge sup-badge-status sup-status-${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
              )}
            </div>
            <div className="sup-sidebar-row">
              <span className="sup-sidebar-label">Priority</span>
              {isStaff ? (
                <select className="sup-sidebar-select" value={ticket.priority} onChange={e => updatePriority(e.target.value)}>
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              ) : (
                <span className={`sup-badge sup-badge-priority sup-priority-${ticket.priority}`}>{PRIORITY_LABELS[ticket.priority]}</span>
              )}
            </div>
            <div className="sup-sidebar-row">
              <span className="sup-sidebar-label">Category</span>
              <span>{CATEGORY_LABELS[ticket.category]}</span>
            </div>
          </div>

          {isStaff && (
            <div className="sup-sidebar-section">
              <h4>Assignment</h4>
              <select
                className="sup-sidebar-select"
                value={ticket.assigned_to || ''}
                onChange={e => updateAssignment(e.target.value)}
              >
                <option value="">Unassigned</option>
                {staffList.map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="sup-sidebar-section">
            <h4>Context</h4>
            <div className="sup-sidebar-row">
              <span className="sup-sidebar-label">Submitted by</span>
              <span>{ticket.user_first_name} {ticket.user_last_name}</span>
            </div>
            {ticket.user_email && (
              <div className="sup-sidebar-row">
                <span className="sup-sidebar-label">Email</span>
                <span className="sup-sidebar-email">{ticket.user_email}</span>
              </div>
            )}
            {ticket.property_name && (
              <div className="sup-sidebar-row">
                <span className="sup-sidebar-label">Property</span>
                <span>{ticket.property_name}</span>
              </div>
            )}
            <div className="sup-sidebar-row">
              <span className="sup-sidebar-label">Created</span>
              <span>{formatDate(ticket.created_at)}</span>
            </div>
            {ticket.resolved_at && (
              <div className="sup-sidebar-row">
                <span className="sup-sidebar-label">Resolved</span>
                <span>{formatDate(ticket.resolved_at)}</span>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          {isStaff && !isClosed && (
            <div className="sup-sidebar-section">
              <h4>Quick Actions</h4>
              <div className="sup-sidebar-actions">
                {ticket.status !== 'waiting_on_user' && (
                  <button className="sup-btn sup-btn-ghost sup-btn-sm sup-btn-block" onClick={() => updateStatus('waiting_on_user')}>
                    Mark Waiting on User
                  </button>
                )}
                {ticket.status !== 'resolved' && (
                  <button className="sup-btn sup-btn-success sup-btn-sm sup-btn-block" onClick={() => updateStatus('resolved')}>
                    <FaCheckCircle /> Resolve
                  </button>
                )}
                <button className="sup-btn sup-btn-ghost sup-btn-sm sup-btn-block" onClick={() => updateStatus('closed')}>
                  <FaTimes /> Close
                </button>
              </div>
            </div>
          )}
        </aside>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetail;
